import { Component, Input, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ClientGuarantorType } from '@components/matter-user-select-or-search/matter-user-select-or-search.component';
import { openClose } from '@core/animations/animations';
import { ICON } from '@core/util/icon';
import { BaseClient, ClientCreationData, Client } from '@models/clients';
import {
	BusinessMatterClient,
	MatterClientType,
	PersonMatterClient,
} from '@models/matter';
import { UntilDestroy } from '@ngneat/until-destroy';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { MatterMortgageGuarantors } from '@state/global/matter_guarantors';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { MatterMortgageDispatch } from '@state/matter/matter_mortgages';
import {
	DropdownOption,
	DropdownResult,
} from 'src/app/shared/atomic/atoms/dropdown-options/dropdown-options.component';
import {
	CreateClientMiniModalComponent,
	CreateClientMiniModalData,
} from '../../../fields/client/create/modal/modal.component';
import { Select } from '@ngxs/store';
import { map, Observable, of } from 'rxjs';
import { DrawerService } from 'src/app/ui/drawer/drawer.service';
import { GlobalProfileComponent } from 'src/app/private/global-profile/global-profile.component';

enum ClientKebabOptionTypes {
	GLOBALVIEW = 'globalView',
	REMOVE = 'remove',
}

const ClientKebabOptions: DropdownOption<ClientKebabOptionTypes>[] = [
	{
		name: 'View global profile',
		value: ClientKebabOptionTypes.GLOBALVIEW,
		classes: ['text-right'],
	},
	{
		name: 'Remove',
		value: ClientKebabOptionTypes.REMOVE,
		classes: ['text-red', 'text-right'],
	},
];

@UntilDestroy()
@Component({
	selector: 'app-mortgage-guarantors',
	templateUrl: './guarantors.component.html',
	animations: [openClose()],
	standalone: false,
})
export class GuarantorsComponent implements OnInit {
	public ICON = ICON;
	public showAddGuarantor = false;
	public clientKebabOptions = ClientKebabOptions;
	public clientGuarantorTypes = ClientGuarantorType;
	public _allowAddEdit = true;
	public zeroGuarantors$ = of(true);
	@SelectSnapshot(SelectedMatter.isLeapMatter) isLeapMatter: boolean;
	@SelectSnapshot(SelectedMatter.id) matterId: string;
	@Select(MatterMortgageGuarantors.getGuarantorsForSelectedMortgage)
	guarantors$: Observable<(PersonMatterClient | BusinessMatterClient)[]>;

	@Input() matterMortgageId;
	@Input() set allowAddEdit(allowEdit: boolean) {
		this._allowAddEdit = allowEdit;

		if (allowEdit) {
			this.clientKebabOptions = ClientKebabOptions;
		} else {
			this.clientKebabOptions = [];
		}
	}

	constructor(
		private matterMortgageDispatch: MatterMortgageDispatch,
		private matDialog: MatDialog,
		private drawerService: DrawerService,
	) {}

	ngOnInit(): void {
		this.zeroGuarantors$ = this.guarantors$.pipe(
			map((guarantors) => {
				if (!guarantors) {
					return true;
				}
				return guarantors.length === 0;
			}),
		);
	}

	toggleShowSearchGuarantor() {
		this.showAddGuarantor = !this.showAddGuarantor;
	}

	addGuarantor($event: BaseClient) {
		if (this.isLeapMatter) {
			this.matterMortgageDispatch.CreateGuarantor(this.matterMortgageId, {
				client: $event as Client,
				matterClientRelationship: {
					clientTypes: [MatterClientType.GUARANTOR],
				},
			});
		} else {
			this.matterMortgageDispatch.AddGuarantor(
				this.matterMortgageId,
				$event.id,
			);
		}

		this.showAddGuarantor = false;
	}

	onKebabSelect(
		clientId: string,
		$event: DropdownResult<DropdownOption<ClientKebabOptionTypes>>,
	) {
		if ($event.result.value === ClientKebabOptionTypes.REMOVE) {
			this.matterMortgageDispatch.RemoveGuarantorFromMortgage(
				this.matterMortgageId,
				clientId,
			);
		} else if ($event.result.value === ClientKebabOptionTypes.GLOBALVIEW) {
			this.drawerService.openDrawer(GlobalProfileComponent, {
				title: `Global Profile `,
				data: { clientId: clientId },
			});
		}
	}

	createGuarantor(searchValue: string) {
		const data: CreateClientMiniModalData = {
			client: {
				name: searchValue,
			},
			matterClientRelationship: {
				clientTypes: [MatterClientType.GUARANTOR],
			},
			callbackFn: async (ev: ClientCreationData) =>
				new Promise((resolve) => {
					if (ev) {
						this.matterMortgageDispatch.CreateGuarantor(
							this.matterMortgageId,
							ev,
						);
						this.showAddGuarantor = false;
					}

					setTimeout(() => {
						resolve();
					}, 1500);
				}),
		};

		this.matDialog.open(CreateClientMiniModalComponent, { data });
	}
}
